import { useEffect, useRef, useState } from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { Button } from "@/components/ui";
import { GUYANE_MAP_CENTER, loadGoogleMapsScript } from "@/utils/googleMapsLoader";
import type { ZoneBoundaryPoint } from "@/utils/zoneBoundary";

export interface ZoneMapPickerProps {
  boundary: ZoneBoundaryPoint[];
  onChange: (boundary: ZoneBoundaryPoint[]) => void;
  onClear?: () => void;
  height?: number;
}

const STROKE = "#1c2118";
const FILL = "#E67E22";

function pointsKey(points: ZoneBoundaryPoint[]): string {
  return points.map((point) => `${point.lat.toFixed(6)},${point.lng.toFixed(6)}`).join(";");
}

function readPath(path: google.maps.MVCArray): ZoneBoundaryPoint[] {
  const points: ZoneBoundaryPoint[] = [];
  for (let index = 0; index < path.getLength(); index++) {
    const point = path.getAt(index);
    points.push({ lat: point.lat(), lng: point.lng() });
  }
  return points;
}

/**
 * Tracé manuel du périmètre de livraison.
 * Clics pour poser les sommets, glisser pour ajuster, clic droit pour retirer un sommet.
 */
export function ZoneMapPicker({
  boundary,
  onChange,
  onClear,
  height = 480,
}: ZoneMapPickerProps): JSX.Element {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);
  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const boundaryRef = useRef(boundary);
  const onChangeRef = useRef(onChange);
  const emittedKey = useRef("");
  const didFit = useRef(false);
  const lastVertexEdit = useRef(0);
  const lastMapClick = useRef(0);
  const rafRef = useRef<number | null>(null);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  onChangeRef.current = onChange;

  const apiRef = useRef<{
    publish: (points: ZoneBoundaryPoint[], render: boolean) => void;
    renderOverlay: (points: ZoneBoundaryPoint[]) => void;
  }>({
    publish: () => {},
    renderOverlay: () => {},
  });

  apiRef.current.publish = (points, render) => {
    emittedKey.current = pointsKey(points);
    boundaryRef.current = points;
    if (render) {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      apiRef.current.renderOverlay(points);
      onChangeRef.current(points);
      return;
    }
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      onChangeRef.current(boundaryRef.current);
    });
  };

  apiRef.current.renderOverlay = (points) => {
    const map = mapInstance.current;
    if (!map) return;

    if (polygonRef.current) {
      google.maps.event.clearInstanceListeners(polygonRef.current.getPath());
      google.maps.event.clearInstanceListeners(polygonRef.current);
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
    markersRef.current.forEach((marker) => {
      google.maps.event.clearInstanceListeners(marker);
      marker.setMap(null);
    });
    markersRef.current = [];

    if (points.length === 0) return;

    if (points.length >= 3) {
      const polygon = new google.maps.Polygon({
        map,
        paths: points,
        editable: true,
        draggable: false,
        clickable: true,
        geodesic: true,
        fillColor: FILL,
        fillOpacity: 0.22,
        strokeColor: STROKE,
        strokeOpacity: 0.95,
        strokeWeight: 2,
      });
      polygonRef.current = polygon;
      const path = polygon.getPath();
      const syncPath = (): void => {
        lastVertexEdit.current = Date.now();
        apiRef.current.publish(readPath(path), false);
      };
      path.addListener("set_at", syncPath);
      path.addListener("insert_at", syncPath);
      path.addListener("remove_at", syncPath);
      polygon.addListener("rightclick", (event) => {
        if (typeof event.vertex !== "number") return;
        const next = readPath(path).filter((_, index) => index !== event.vertex);
        apiRef.current.publish(next, true);
      });
      return;
    }

    polylineRef.current = new google.maps.Polyline({
      map,
      path: points,
      geodesic: true,
      strokeColor: STROKE,
      strokeOpacity: 0.95,
      strokeWeight: 2,
    });
    markersRef.current = points.map(
      (point, index) =>
        new google.maps.Marker({
          map,
          position: point,
          draggable: true,
          label: String(index + 1),
          title: "Sommet du périmètre",
        }),
    );
    markersRef.current.forEach((marker, index) => {
      marker.addListener("dragend", () => {
        const position = marker.getPosition();
        if (!position) return;
        const next = boundaryRef.current.map((current, pointIndex) =>
          pointIndex === index
            ? { lat: position.lat(), lng: position.lng() }
            : current,
        );
        apiRef.current.publish(next, false);
      });
    });
  };

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? "";

  useEffect(() => {
    if (!apiKey) {
      setError("Clé Google Maps non configurée (VITE_GOOGLE_MAPS_API_KEY).");
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function init(): Promise<void> {
      try {
        await loadGoogleMapsScript(apiKey);
        if (cancelled || !mapRef.current) return;

        const map = new google.maps.Map(mapRef.current, {
          center: GUYANE_MAP_CENTER,
          zoom: 12,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          disableDoubleClickZoom: true,
          clickableIcons: false,
          draggableCursor: "crosshair",
          gestureHandling: "greedy",
        });
        mapInstance.current = map;

        map.addListener("click", (event) => {
          if (!event.latLng) return;
          const now = Date.now();
          if (now - lastVertexEdit.current < 250) return;
          if (now - lastMapClick.current < 180) return;
          lastMapClick.current = now;
          const next = [
            ...boundaryRef.current,
            { lat: event.latLng.lat(), lng: event.latLng.lng() },
          ];
          apiRef.current.publish(next, true);
        });

        setReady(true);
        setLoading(false);
      } catch {
        if (!cancelled) {
          setError("Impossible de charger Google Maps.");
          setLoading(false);
        }
      }
    }

    void init();
    return () => {
      cancelled = true;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [apiKey]);

  useEffect(() => {
    if (!ready) return;
    const key = pointsKey(boundary);
    if (key === emittedKey.current) return;
    emittedKey.current = key;
    boundaryRef.current = boundary;
    apiRef.current.renderOverlay(boundary);
    if (!didFit.current && boundary.length >= 3 && mapInstance.current) {
      didFit.current = true;
      const bounds = new google.maps.LatLngBounds();
      boundary.forEach((point) => bounds.extend(point));
      mapInstance.current.fitBounds(bounds, 48);
    }
  }, [boundary, ready]);

  function undoLastPoint(): void {
    apiRef.current.publish(boundaryRef.current.slice(0, -1), true);
  }

  function clearBoundary(): void {
    apiRef.current.publish([], true);
    onClear?.();
  }

  if (error) {
    return <Alert severity="warning">{error}</Alert>;
  }

  const remaining = 3 - boundary.length;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Cliquez sur la carte pour placer les sommets. Le périmètre se ferme à partir de 3 points.
        Faites glisser un sommet pour l&apos;ajuster, le point au milieu d&apos;un côté pour en
        ajouter un, et faites un clic droit sur un sommet pour le supprimer.
      </Typography>
      <Box sx={{ position: "relative", height, borderRadius: 1, overflow: "hidden" }}>
        {loading ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "action.hover",
              zIndex: 1,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : null}
        <Box ref={mapRef} role="application" aria-label="Carte du périmètre de livraison" sx={{ width: "100%", height: "100%" }} />
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mt: 1.5 }}>
        <Button type="button" variant="outline" size="small" onClick={undoLastPoint} disabled={boundary.length === 0}>
          Annuler le dernier point
        </Button>
        <Button type="button" variant="outline" size="small" onClick={clearBoundary} disabled={boundary.length === 0}>
          Effacer le tracé
        </Button>
        <Typography variant="caption" color="text.secondary">
          {boundary.length === 0
            ? "Aucun sommet"
            : boundary.length < 3
              ? remaining === 1
                ? "1 point restant pour fermer le périmètre"
                : `${remaining} points restants pour fermer le périmètre`
              : `Périmètre fermé · ${boundary.length} sommets`}
        </Typography>
      </Box>
    </Box>
  );
}
